import _regenerator from "/usr/app/node_modules/@babel/runtime/helpers/esm/regenerator.js";
import _asyncToGenerator from "/usr/app/node_modules/@babel/runtime/helpers/esm/asyncToGenerator.js";
import React from "react";
var __jsx = React.createElement;
import { useEffect } from "react";
import useHelpers from "@hooks/useHelpers";
import Loader from "@componentsShared/Loader/Loader";
import ResultPageShell from "./components/ResultPageShell";
import { api__cancelAmwalSplitOrder, api__getSplitPaymentSummary } from "@/apis/splitPayment/splitPayment";
import { classifyResultStatus } from "./constants/resultConstants";

// Amwal always bounces back to success_url — even on decline or cancel (unlike
// tabby/tamara, which honor failure_url/cancel_url). So the redirect can't be
// trusted; the summary's session_status is the source of truth. This gateway is
// the checkout success_url landing: it fetches the summary, then forwards to the
// real result screen so the buyer never sees the wrong one flash.

/**
 * Loader-only gateway between the provider checkout and the split-payment result
 * screens. Reads order_id from the query, looks up /split-payment/summary, and
 * replaces itself with success / failed / try-another based on session_status —
 * carrying the original query params through so the target screen + its tracking
 * keep working. No visible content besides a spinner, so there's no success flash.
 */
var SplitPaymentResultRouter = function SplitPaymentResultRouter() {
  if (__IS_SSR__) return null;
  var _useHelpers = useHelpers(),
    navigateTo = _useHelpers.navigateTo;
  useEffect(function () {
    var search = window.location.search;
    var params = new URLSearchParams(search);
    var orderId = params.get("s_order_id") || params.get("order_id");
    var provider = (params.get("provider") || "").toLowerCase();
    var go = function go(route) {
      return navigateTo("/site/split-payment/".concat(route).concat(search));
    };

    // Fail screen carries failure_reason (cancel | failure) on the URL so its
    // tracking fires the right reason without re-reading session_status.
    var goFailed = function goFailed(reason) {
      var failParams = new URLSearchParams(search);
      failParams.set("failure_reason", reason);
      navigateTo("/site/split-payment/failed?".concat(failParams.toString()));
    };
    if (!orderId) {
      goFailed("failure");
      return;
    }

    // tabby/tamara honor their own webhooks, which can lag behind the redirect —
    // the summary may still read pending/cancel for a few seconds after a real
    // success. For these providers we poll until session_status resolves to
    // success (routing there immediately), capped so a genuine decline still
    // lands on the fail screen instead of looping forever.
    var shouldRetry = provider === "tabby" || provider === "tamara";
    var RETRY_INTERVAL_MS = 2000;
    var MAX_ELAPSED_MS = 30000; // stop retrying after ~30s of non-success.

    var cancelled = false;
    var timer;
    var startedAt = Date.now();
    // Amwal pending → cancel once, then re-read (mirrors AmwalPaymentStatusPage):
    // a session left pending means the buyer bailed out of the amwal checkout, so
    // canceling flips the summary to "canceled" and the reason resolves to cancel.
    var amwalCancelDone = false;
    var _poll = function poll() {
      api__getSplitPaymentSummary(orderId, provider).then(/*#__PURE__*/function () {
        var _ref = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee(data) {
          var _data, _data2;
          var kind, _t;
          return _regenerator().w(function (_context) {
            while (1) switch (_context.p = _context.n) {
              case 0:
                if (!cancelled) {
                  _context.n = 1;
                  break;
                }
                return _context.a(2);
              case 1:
                if (!(provider === "amwal" && (((_data = data) === null || _data === void 0 ? void 0 : _data.session_status) || "").toLowerCase() === "pending" && !amwalCancelDone)) {
                  _context.n = 8;
                  break;
                }
                amwalCancelDone = true;
                _context.p = 2;
                _context.n = 3;
                return api__cancelAmwalSplitOrder({
                  order_id: Number(orderId),
                  status: "pending",
                  provider: "amwal"
                });
              case 3:
                _context.n = 5;
                break;
              case 4:
                _context.p = 4;
                _t = _context.v;
                console.error("Error canceling pending amwal split-payment order:", _t);
              case 5:
                if (!cancelled) {
                  _context.n = 6;
                  break;
                }
                return _context.a(2);
              case 6:
                _context.n = 7;
                return api__getSplitPaymentSummary(orderId, provider);
              case 7:
                data = _context.v;
                if (!cancelled) {
                  _context.n = 8;
                  break;
                }
                return _context.a(2);
              case 8:
                kind = classifyResultStatus((_data2 = data) === null || _data2 === void 0 ? void 0 : _data2.session_status);
                if (!(kind === "success")) {
                  _context.n = 9;
                  break;
                }
                return _context.a(2, go("success"));
              case 9:
                if (!(shouldRetry && Date.now() - startedAt < MAX_ELAPSED_MS)) {
                  _context.n = 10;
                  break;
                }
                timer = setTimeout(_poll, RETRY_INTERVAL_MS);
                return _context.a(2);
              case 10:
                // Cancel + rejection both show the fail screen; pass the resolved
                // reason so tracking reports cancel vs failure. Never route to try-another.
                goFailed(kind === "cancel" ? "cancel" : "failure");
              case 11:
                return _context.a(2);
            }
          }, _callee, null, [[2, 4]]);
        }));
        return function (_x) {
          return _ref.apply(this, arguments);
        };
      }())["catch"](function (e) {
        if (cancelled) return;
        if (shouldRetry && Date.now() - startedAt < MAX_ELAPSED_MS) {
          timer = setTimeout(_poll, RETRY_INTERVAL_MS);
          return;
        }
        console.error("Error routing split-payment result:", e);
        goFailed("failure");
      });
    };

    // Give the payment webhook a head start (~2s) before the first read —
    // otherwise the status can still be stale and route wrong.
    timer = setTimeout(_poll, 2000);
    return function () {
      cancelled = true;
      clearTimeout(timer);
    };
  }, []);
  return /*#__PURE__*/React.createElement(ResultPageShell, {
    bgClass: "!bg-white"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex flex-1 items-center justify-center"
  }, /*#__PURE__*/React.createElement(Loader, {
    className: "scale-[2]"
  })));
};
export default SplitPaymentResultRouter;